import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useTable, usePagination, useFilters, useSortBy} from 'react-table';
import {useListContext} from 'react-admin';
import {setCurrentTableData} from '../../store/actions';
import IconSortArrowDown from '../../css/images/IconSortArrowDown';
import IconSortArrowUp from '../../css/images/IconSortArrowUp';
import ListLoading from '../ListLoading';
import ListMessage from '../ListMessage';
import {DefaultColumnFilter} from '../../utils/tableFunctions';
import CssBaseline from '@material-ui/core/CssBaseline';
import MaUTable from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';

/**
 * A key for a column set that changes only when a column is added, removed,
 * reordered or resized, not when its definition is rebuilt with new function
 * identities.
 */
export const columnShapeKey = (columns) => columns.map((column) => [
    column.id ?? '',
    (typeof column.accessor === 'string') ? column.accessor : '',
    (typeof column.Header === 'string') ? column.Header : '',
    column.width ?? '',
    column.ClassName ?? '',
    column.disableFilters ? 'nf' : '',
    column.disableSortBy ? 'ns' : ''
].join(':')).join('|');

// Function-valued column properties react-table calls as plain functions;
// stableColumns forwards each to the newest definition (web-admin#43).
const FORWARDED_FUNCTIONS = ['Cell', 'Header', 'Filter', 'accessor', 'sortType', 'filter'];

/**
 * Column objects that stay the same across renders while their function
 * properties follow the newest definition at the same position.
 *
 * - `Cell`, `Header`, `Filter`, `accessor`, `sortType` and `filter`, when
 *   functions, become wrappers that call the newest definition's function.
 *   Properties react-table reads off a filter function, such as
 *   `autoRemove`, are kept. Rendering the newest Cell or Filter as an element
 *   instead would remount an inline one, and lose a filter input's focus, on
 *   every render.
 * - Every other property keeps the value it had when the column shape last
 *   changed (see columnShapeKey).
 */
export const stableColumns = (columns, latestColumns) => columns.map((column, index) => {
    const latest = (name) => {
        const current = latestColumns.current[index];
        return (current && typeof current[name] === 'function') ? current[name] : column[name];
    };
    const stable = {...column};
    FORWARDED_FUNCTIONS.forEach((name) => {
        if (typeof column[name] === 'function') {
            stable[name] = Object.assign((...args) => latest(name)(...args), column[name]);
        }
    });
    return stable;
});

/**
 * A component that renders a React Table from React-Admin data structure
 *
 * Column contract (web-admin#40, #43). Callers may rebuild `columns` on every
 * render; the table keeps the same column objects, and so keeps its cells
 * mounted, until the column shape changes: the order of ids, string
 * accessors, string headers, widths, `ClassName`, `disableFilters` and
 * `disableSortBy` (columnShapeKey).
 *
 * - `Cell`, `Header`, `Filter`, `accessor`, `sortType` and `filter` may be
 *   new functions on every render and may close over the caller's state:
 *   the table always calls the newest one.
 * - react-table re-runs `accessor`, `filter` and `sortType` only when the
 *   data, the filter values or the sort change. When what one of them
 *   returns depends on caller state (a filter on the selected client, an
 *   accessor behind a toggle), put that state in the column `id`, for
 *   example `id: 'qty-' + clientId`, so the shape changes and the column is
 *   rebuilt.
 * - Any other property (`sortBy`, a string `Cell`, a number `width` read by
 *   the caller) is read when the shape changes, not on every render.
 * - A `Cell`, `Header` or `Filter` is called as a function inside the
 *   table's own component, so its hooks belong to that component: one
 *   column's `Cell` must always call the same hooks. Switching between Cells
 *   with different hooks needs a shape change too.
 *
 * @param props
 * @return {JSX.Element|null}
 * @constructor
 */
const DataTable = (props) => {
    const {
        columns,
        perPage,
        sort,
        listFilters = null,
        listFilter = null,
        messageEmpty = '',
        messageFilter = '',
        instructions = '',
        fixedLayout,
        manualQuickFilters,
        manualSortAndPagination,
        onRowsChange,
        rowStyle,
        setCurrentTableData,
        storeForExport,
        storeSort
    } = props;

    const {
        basePath,
        data,
        filterValues,
        ids,
        loaded,
        setFilters,
        setPage,
        setSort,
        total
    } = useListContext(props);

    const [dataList, setDataList] = useState([]);
    const [currentTableList, setCurrentTableList] = useState([]);
    const [manualPageIndex, setManualPageIndex] = useState(0);

    const lastFilterRef = useRef(null);

    // Callback: Store current table data in Redux
    const storeCurrentTableData = useCallback((currentList) => {
        setCurrentTableData(currentList);
    }, [setCurrentTableData]);

    // Effect: Update table list state upon new data
    useEffect(() => {
        // Create an array of data the table can use based on React-Admin's ids and data
        const setTableData = () => {
            let list = [];
            let idLength = ids.length;
            if (idLength > 0) {
                // Assemble an array based on React-Admin ids and data
                for (let i = 0; i < idLength; i++) {
                    let id = ids[i];
                    let item = data[id];
                    if (item && item.id !== '') {
                        list.push(item);
                    }
                }
            }
            setDataList(list);
        };

        return setTableData();

    }, [ids, data]);

    // Memoized Table Data
    const tableData = useMemo(() => dataList, [dataList]);

    // Stable Table Columns (web-admin#40)
    //
    // Callers build their columns inline, so `columns` is a new array, and every
    // Cell a new function, on each render. react-table rebuilds on a new array,
    // and React treats a new Cell function as a new component type, so every
    // cell unmounted and mounted again on every render: three thumbnail
    // requests per product row on one list load. Memoizing on `columns` itself
    // never held. Memoizing in each caller would freeze the values the cells
    // read (selection, the clock, filters).
    //
    // So the column objects stay the same while the column set does (same
    // order of ids, accessors, headers and widths), and their functions call
    // the latest definition, which keeps the cells mounted and current. The
    // contract callers rely on is in the component's JSDoc above.
    const latestColumns = useRef(columns);
    latestColumns.current = columns;
    const columnsShape = columnShapeKey(columns);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuilt when the shape changes; content is read through latestColumns
    const tableColumns = useMemo(() => stableColumns(columns, latestColumns), [columnsShape]);

    // Fixed layout: size columns from their `width` definitions instead of from
    // whatever content is on the current page, so the table keeps its shape
    // while sorting and paging. With every column sized, the table can't
    // shrink below their sum (the container scrolls instead).
    const columnWidths = tableColumns.map(column => column.width);
    const allColumnsSized = columnWidths.every(width => typeof width === 'number');
    const tableStyle = (fixedLayout)
        ? {tableLayout: 'fixed', minWidth: (allColumnsSized) ? columnWidths.reduce((sum, width) => sum + width, 0) : undefined}
        : undefined;

    // Default Sort column
    const sortBy = (sort) ? {id: sort.field, desc: (sort.order) === 'DESC'} : {id: 'id', desc: false};

    // Initial sort & pagination values
    const initialState = {
        pageIndex: 0,
        pageSize: (perPage) ? perPage : 20,
        sortBy: [sortBy]
    };

    // Default Filter
    const defaultColumn = useMemo(
        () => ({
            Filter: DefaultColumnFilter
        }), []
    );

    // Table Definition
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        rows,
        gotoPage,
        state: {pageIndex}
    } = useTable(
        {
            columns: tableColumns,
            data: tableData,
            defaultColumn: defaultColumn,
            initialState: initialState,
            manualFilters: manualQuickFilters,
            manualPagination: manualSortAndPagination,
            manualSortBy: manualSortAndPagination
        },
        useFilters,
        useSortBy,
        usePagination
    );

    // Effect: Store currently filtered and sorted rows
    useEffect(() => {
        const storeCurrentRows = () => {
            let list = [];
            if (page.length > 0) {
                page.forEach(function (row, idx) {
                    if (row.original?.id) {
                        list.push(row.original);
                    }
                });
            }
            const isNew = (JSON.stringify(list) !== JSON.stringify(currentTableList));
            if (list.length > 0 && isNew) {
                setCurrentTableList(list);
                storeCurrentTableData(list);
            }
        };

        if (storeForExport && page && page.length > 0) {
            storeCurrentRows();
        }
    }, [page, currentTableList, storeForExport, storeCurrentTableData]);

    // Effect: Report the rows the table holds after its own filtering and
    // sorting, every page, in on-screen order (e.g. so an Export can write
    // exactly what is on screen). Column filters and header clicks happen in
    // the browser, so React-Admin's list state knows nothing about them.
    // Not the storeForExport path, which stores the current page only.
    useEffect(() => {
        if (!onRowsChange) {
            return;
        }
        onRowsChange(rows.map(row => row.original));
    }, [rows, onRowsChange]);

    // Display loading before resource has responded
    if (loaded === false) {
        return (
            <ListLoading/>
        )
    }

    // Pass sort click to parent if sorting is manual
    const handleManualSort = (id, sortBy, isSorted, isSortedDesc) => {
        const sortOrder = (isSorted) ? (isSortedDesc) ? 'DESC' : 'ASC' : 'ASC';
        const sortValue = (sortBy) ? sortBy : id;
        setSort(sortValue, sortOrder);
        if (manualPageIndex > 0) {
            setPage(1);
            setManualPageIndex(0);
        }
    };

    // Pass sort click to parent if sorting should be stored
    const handleSortStore = (id, sortBy, isSorted, isSortedDesc) => {
        const sortOrder = (isSorted) ? (isSortedDesc) ? 'DESC' : 'ASC' : 'ASC';
        const sortValue = (sortBy) ? sortBy : id;
        setSort(sortValue, sortOrder);
    };

    // Pass pagination click to parent if pagination is manual
    const handleManualPagination = (newPage) => {
        const toPage = Number(newPage);
        setPage(toPage + 1);
        setManualPageIndex(toPage);
    };

    const handleManualQuickFilters = (e, id, value) => {
        lastFilterRef.current = (value && value !== '') ? id : null;
        const updatedFilters = JSON.parse(JSON.stringify(filterValues));
        updatedFilters['s_' + id] = value;
        setFilters(updatedFilters, undefined, true);
    };

    // Set additional data to pass to cells
    const cellProps = {
        basePath: basePath
    };

    // Display message or prompt if empty
    let message;
    if (loaded && (ids.length === 0 || total === 0)) {
        if (listFilters && listFilter && props[listFilters] && props[listFilters][listFilter] && messageEmpty) {
            message = messageEmpty;
        } else if (listFilters && listFilter) {
            message = messageFilter;
        } else {
            message = messageEmpty;
        }
    }

    // Render table
    return (
        <>
            {instructions &&
                <p className="data-table-instructions">{instructions}</p>
            }
            {message &&
                <ListMessage text={message}/>
            }
            <div className={`list-table-container${(fixedLayout) ? ' list-table-container-fixed' : ''}`}>
                <CssBaseline/>
                <MaUTable {...getTableProps()} size="small"
                          className={`list-table${(fixedLayout) ? ' list-table-fixed' : ''}`}
                          style={tableStyle}>

                    {fixedLayout &&
                        <colgroup>
                            {tableColumns.map((column, idx) => (
                                <col key={column.id || (typeof column.accessor === 'string' ? column.accessor : idx)}
                                     style={(typeof column.width === 'number') ? {width: column.width} : undefined}/>
                            ))}
                        </colgroup>
                    }

                    <TableHead>
                        {headerGroups.map(headerGroup => (
                            <TableRow {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <TableCell {...column.getHeaderProps()} key={`cell-${column.id}`}>
                                        {/* Table Header Label & Sort */}
                                        <div {...column.getSortByToggleProps()}
                                             {...manualSortAndPagination && {
                                                 onClick: () => handleManualSort(column.id, column.sortBy, column.isSorted, column.isSortedDesc)
                                             }}
                                             {...storeSort && {
                                                 onClick: () => handleSortStore(column.id, column.sortBy, column.isSorted, column.isSortedDesc)
                                             }}
                                        >
                                            {column.render('Header')}
                                            {column.canSort &&
                                                <span className="sort-icon">
                                                {column.isSorted
                                                    ? column.isSortedDesc
                                                        ? <IconSortArrowDown/>
                                                        : <IconSortArrowUp/>
                                                    : <IconSortArrowDown classes="disabled"/>}
                                            </span>
                                            }
                                        </div>
                                        {/* Table Header Filter */}
                                        <div className="column-filter" key={`column-filter-${column.id}`}>
                                            {column.canFilter && !manualQuickFilters
                                                ? column.render('Filter')
                                                : null
                                            }
                                            {column.canFilter && manualQuickFilters
                                                ? column.render(({column}) => (
                                                    <input
                                                        key={`s_${column.id}`}
                                                        id={`s_${column.id}`}
                                                        name={`s_${column.id}`}
                                                        defaultValue={(filterValues && filterValues['s_' + column.id]) || undefined}
                                                        autoFocus={!!(lastFilterRef.current && lastFilterRef.current === column.id)}
                                                        onChange={e => {
                                                            e.preventDefault();
                                                            handleManualQuickFilters(e, column.id, e.target.value);
                                                        }}
                                                        placeholder="Search"
                                                        className="table-filter-default"
                                                        style={{display: 'block'}}
                                                    />
                                                ))
                                                : null
                                            }
                                        </div>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>

                    {/* Table Body with paginated rows */}
                    <TableBody {...getTableBodyProps()}>
                        {page.map((row, i) => {
                            prepareRow(row)
                            return (
                                <TableRow {...row.getRowProps()} style={(rowStyle) ? rowStyle(row) : undefined}>
                                    {row.cells.map(cell => {
                                        const tdClass = (cell.column.ClassName) ? cell.column.ClassName : undefined;
                                        return (
                                            <TableCell {...cell.getCellProps()} className={tdClass}>
                                                {cell.render('Cell', cellProps)}
                                            </TableCell>
                                        )
                                    })}
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </MaUTable>

                {/* Table Pagination with optional manual pagination */}
                <TablePagination
                    component="div"
                    count={(manualSortAndPagination) ? total : rows.length}
                    page={(manualSortAndPagination) ? manualPageIndex : pageIndex}
                    rowsPerPage={initialState.pageSize}
                    rowsPerPageOptions={[]}
                    onPageChange={(e, newPage) => {
                        if (manualSortAndPagination) {
                            handleManualPagination(newPage);
                        } else {
                            gotoPage((newPage) ? Number(newPage) : 0);
                        }
                    }}
                />
            </div>
        </>
    )
};

const mapDispatchToProps = {
    setCurrentTableData: setCurrentTableData
};

DataTable.propTypes = {
    basePath: PropTypes.string,
    body: PropTypes.element,
    children: PropTypes.node,
    classes: PropTypes.object,
    className: PropTypes.string,
    currentSort: PropTypes.shape({
        field: PropTypes.string,
        order: PropTypes.string,
    }),
    // react-table column definitions. They may be rebuilt on every render;
    // only Cell, Header, Filter, accessor, sortType and filter follow the
    // newest definition between shape changes. See the column contract in the
    // DataTable JSDoc (web-admin#43).
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    data: PropTypes.object,
    fixedLayout: PropTypes.bool,
    instructions: PropTypes.string,
    manualQuickFilters: PropTypes.bool,
    manualSortAndPagination: PropTypes.bool,
    onRowsChange: PropTypes.func,
    onUpdate: PropTypes.func,
    storeForExport: PropTypes.bool,
    storeStore: PropTypes.bool,
    // Ignored
    expand: PropTypes.oneOfType([PropTypes.element, PropTypes.elementType]),
    hasBulkActions: PropTypes.bool,
    hover: PropTypes.bool,
    ids: PropTypes.arrayOf(PropTypes.any),
    loading: PropTypes.bool,
    onSelect: PropTypes.func,
    onToggleItem: PropTypes.func,
    resource: PropTypes.string,
    rowClick: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    rowStyle: PropTypes.func,
    selectedIds: PropTypes.arrayOf(PropTypes.any),
    setPage: PropTypes.func,
    setPerPage: PropTypes.func,
    setSort: PropTypes.func,
    total: PropTypes.number,
    version: PropTypes.number,
    isRowSelectable: PropTypes.func,
};

DataTable.defaultProps = {
    fixedLayout: false,
    manualQuickFilters: false,
    manualSortAndPagination: false,
    storeForExport: false,
    storeSort: false
};

export default connect(
    null,
    mapDispatchToProps
)(DataTable);
